import React, { useRef, useEffect } from 'react'
import Typography from '../atoms/Typography'
import UpgradePlanDialog from '../organisms/dialogs/UpgradePlanDialog'
import { ISubscriptionPlan } from 'src/enviroment/api/services/subscriptionsApi'
import { PrimaryButton } from '../atoms/buttons/PrimaryButton'
import { IconCheck, IconX, IconChevronDown, IconChevronUp } from '@tabler/icons-react'
import { cn } from 'src/enviroment/lib/utils'

interface PlanFeature {
  name: string
  value: string | boolean
}

const PLAN_FEATURES: Record<string, PlanFeature[]> = {
  FREE: [
    { name: 'COPA AI', value: true },
    { name: 'Custom Logo', value: false },
    { name: 'Team Members', value: false },
    { name: 'Geospatial Data', value: 'Basic Access' },
    { name: 'NBS Data Metrics', value: 'Basic Access' },
    { name: 'Carbon Monitoring', value: 'Limited area 50,000 Ha' },
    { name: 'Datasets Resolution', value: 'Lower' },
    { name: 'Data Rooms', value: '500 MB' },
  ],
  PRO: [
    { name: 'COPA AI', value: true },
    { name: 'Custom Logo', value: true },
    { name: 'Team Members', value: '10 Team Members' },
    { name: 'Geospatial Data', value: true },
    { name: 'NBS Data Metrics', value: true },
    { name: 'Carbon Monitoring', value: 'Higher area 1M Ha' },
    { name: 'Datasets Resolution', value: 'High resolution' },
    { name: 'Data Rooms', value: '5 GB' },
  ],
  ENTERPRISE: [
    { name: 'COPA AI', value: true },
    { name: 'Custom Logo', value: true },
    { name: 'Team Members', value: 'Unlimited' },
    { name: 'Geospatial Data', value: true },
    { name: 'NBS Data Metrics', value: true },
    { name: 'Carbon Monitoring', value: 'Unlimited' },
    { name: 'Datasets Resolution', value: 'Highest resolution' },
    { name: 'Data Rooms', value: 'Unlimited' },
  ],
}

const VISIBLE_FEATURES_COUNT = 4

interface SubscriptionCardProps {
  subscription: ISubscriptionPlan
  isCurrentPlan: boolean
  isExpanded: boolean
  onToggleExpand: () => void
  onEnterpriseClick?: () => void
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscription,
  isCurrentPlan,
  isExpanded,
  onToggleExpand,
  onEnterpriseClick,
}) => {
  const featuresEndRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const prevExpandedRef = useRef(isExpanded)

  const { name, plan, priceInCents, description, interval } = subscription

  const isEnterprise = plan === 'ENTERPRISE'
  const isPro = plan === 'PRO'
  const features = PLAN_FEATURES[plan] || []
  const hiddenFeatures = features.slice(VISIBLE_FEATURES_COUNT)
  const hasMoreFeatures = features.length > VISIBLE_FEATURES_COUNT

  useEffect(() => {
    if (prevExpandedRef.current !== isExpanded) {
      if (isExpanded) {
        setTimeout(() => {
          featuresEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }, 50)
      } else {
        setTimeout(() => {
          cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }, 50)
      }
      prevExpandedRef.current = isExpanded
    }
  }, [isExpanded])

  const formatPrice = () => {
    if (plan === 'FREE') return 'Free'
    if (plan === 'ENTERPRISE') return "Let's Talk"

    return `$${priceInCents / 100}`
  }

  const getPriceSubtext = () => {
    if (plan === 'ENTERPRISE') return ''

    return `/${interval}`
  }

  const renderFeatureValue = (value: string | boolean) => {
    if (typeof value === 'boolean') {
      if (value) {
        return (
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-light-green-500">
            <IconCheck className="h-3.5 w-3.5 text-light-green-50" />
          </div>
        )
      }
      return (
        <div
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
            isCurrentPlan ? 'bg-gray-400' : 'bg-gray-600',
          )}
        >
          <IconX className={cn('h-3.5 w-3.5', isCurrentPlan ? 'text-gray-100' : 'text-gray-400')} />
        </div>
      )
    }
    return (
      <Typography
        variant="base"
        className={cn('text-right font-medium', isCurrentPlan ? 'text-gray-700' : 'text-light-green-400')}
      >
        {value}
      </Typography>
    )
  }

  const renderButton = () => {
    if (isCurrentPlan) {
      return (
        <div className="rounded-full bg-gray-800 p-4 text-center">
          <Typography variant="base-semibold" className="text-gray-50">
            Current Plan
          </Typography>
        </div>
      )
    }

    if (isEnterprise) {
      return (
        <PrimaryButton
          size="lg"
          className="w-full transform transition-all duration-200 hover:shadow-lg"
          onClick={onEnterpriseClick}
        >
          Schedule a call with us
        </PrimaryButton>
      )
    }

    return <UpgradePlanDialog subscription={subscription} />
  }

  const renderFeatureRow = (feature: PlanFeature, index: number, isLast: boolean) => (
    <div
      key={feature.name}
      className={cn(
        'flex items-center justify-between gap-4 py-2.5',
        !isLast && (isCurrentPlan ? 'border-b border-gray-400/30' : 'border-b border-gray-700'),
      )}
    >
      <Typography variant="sm" className={cn(isCurrentPlan ? 'text-gray-700' : 'text-gray-300')}>
        {feature.name}
      </Typography>
      {renderFeatureValue(feature.value)}
    </div>
  )

  return (
    <div
      ref={cardRef}
      className={cn(
        'relative flex min-w-[320px] flex-1 basis-0 flex-col rounded-2xl p-4 transition-all duration-300 ease-in-out hover:scale-[1.02] sm:p-6 md:p-8',
        isCurrentPlan && 'bg-light-green-200 shadow-lg',
        isPro && !isCurrentPlan && 'bg-gray-900 ring-2 ring-light-green-500',
        !isCurrentPlan && !isPro && 'bg-gray-900',
      )}
    >
      {isPro && !isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="rounded-full bg-light-green-500 px-4 py-1 text-sm font-semibold text-gray-900 ring-2 ring-inset ring-light-green-600">
            Most Popular
          </span>
        </div>
      )}

      {/* Header */}
      <Typography variant="xl" className={cn('mb-2', isCurrentPlan ? 'text-gray-900' : 'text-light-green-50')}>
        {name}
      </Typography>

      {/* Price Container */}
      <div className={cn('mb-3 flex items-baseline gap-1', isCurrentPlan ? 'text-gray-900' : 'text-light-green-50')}>
        <Typography variant="2xl-semibold" element="span" className="flex items-baseline gap-1 text-4xl font-bold">
          {formatPrice()}
          <Typography variant="base" element="span" className="text-gray-500">
            {getPriceSubtext()}
          </Typography>
        </Typography>
      </div>

      {/* Description */}
      <Typography
        variant="sm"
        element="p"
        className={cn('mb-5 line-clamp-2', isCurrentPlan ? 'text-gray-800' : 'text-gray-100')}
      >
        {description}
      </Typography>

      {/* CTA Button */}
      <div className="mb-6 mt-auto">{renderButton()}</div>

      {/* Features List */}
      <div>
        {features
          .slice(0, VISIBLE_FEATURES_COUNT)
          .map((feature, index) =>
            renderFeatureRow(feature, index, !isExpanded && index === VISIBLE_FEATURES_COUNT - 1),
          )}

        {hasMoreFeatures && (
          <div
            className={cn(
              'grid transition-all duration-300 ease-in-out',
              isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
            )}
          >
            <div className="overflow-hidden">
              {hiddenFeatures.map((feature, index) =>
                renderFeatureRow(feature, index + VISIBLE_FEATURES_COUNT, index === hiddenFeatures.length - 1),
              )}
            </div>
          </div>
        )}

        {hasMoreFeatures && (
          <button
            onClick={onToggleExpand}
            className={cn(
              'mt-3 flex w-full items-center justify-center gap-1 text-sm font-medium transition-colors',
              isCurrentPlan ? 'text-gray-600 hover:text-gray-800' : 'text-gray-400 hover:text-light-green-400',
            )}
          >
            {isExpanded ? (
              <>
                Show less <IconChevronUp className="h-4 w-4 transition-transform duration-300" />
              </>
            ) : (
              <>
                See all features <IconChevronDown className="h-4 w-4 transition-transform duration-300" />
              </>
            )}
          </button>
        )}

        {/* Scroll anchor */}
        <div ref={featuresEndRef} />
      </div>
    </div>
  )
}

export default SubscriptionCard
